import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import PaymentSuccessModal from './PaymentSuccessModal';
import ContactFormModal from './ContactFormModal';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import colors from '../theme/colors';
import { IconMoon, IconStar, IconPrinter, IconCheck, IconMail, IconHeart, IconBulb } from './icons';

// Strip decorative emojis from translated strings (tiles render SVG icons instead)
const stripEmoji = (s) => (s || '').replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}]/gu, '').trim();

const PremiumContainer = styled.div`
  position: fixed;
  top: 70px; /* Just under the login/logout button */
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    right: 20px;
    z-index: 10001;
  }
`;

const PillowButton = styled.button`
  background: none;
  border: none;
  color: ${colors.accent.primary};
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  position: relative;
  padding: 0;
  margin: 0;
  outline: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 29px;
    height: 29px;
  }
`;

const PremiumLabel = styled.div`
  margin-top: 2px;
  color: ${colors.accent.primary}; /* match Subtitle color */
  font-size: 8px;
  font-weight: 600;
  text-shadow: 0 1px 5px rgba(45, 27, 61, 0.6);
  line-height: 1;
  text-align: center;
`;

const DropdownMenu = styled(motion.div)`
  position: fixed;
  background: ${colors.base.themePrimaryTransparent};
  border: 1px solid ${colors.border.light};
  border-radius: 50px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 8px;
  min-width: 280px;
  z-index: 15000;

  @media (max-width: 768px) {
    min-width: 200px !important;
    width: min(85vw, 240px) !important;
    max-width: 85vw !important;
    max-height: calc(100vh - 140px) !important;
    overflow: auto !important;
    padding: 6px !important;
  }
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.$active ? colors.button.aboutBgHover : 'transparent'};
  border: none;
  border-radius: 30px;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: ${colors.text.light};
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  margin: 4px 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  span {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    padding: 8px 10px;
    font-size: 12px;
    gap: 8px;
    margin: 2px 0;

    span {
      font-size: 14px;
    }
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.3);
  margin: 8px 0;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 12000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  /* Same treatment as the main header: translucent theme blue + white streaks */
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  border-radius: 16px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${colors.border.light};
  will-change: transform;
  transform: translateZ(0);
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar */
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: ${colors.ui.scrollbarTrackDark}; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: ${colors.ui.scrollbarThumb}; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: ${colors.ui.scrollbarThumbHover}; }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const DonateSection = styled.div`
  margin-top: 25px;
  padding: 25px;
  background: ${colors.base.themeTertiary};
  border-radius: 12px;
  text-align: center;
  box-shadow: ${colors.effects.shadow};

  h3 {
    color: ${colors.base.themePrimary};
    margin: 0 0 15px 0;
    font-size: 1.1rem;
    font-weight: 600;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  p {
    color: ${colors.base.themePrimary};
    margin: 0 0 12px 0;
    font-size: 0.7rem;
    text-align: justify;
  }

  .paypal-button {
    width: 100% !important;
    max-width: none !important;
  }

  [id*="paypal-container"] {
    width: 100% !important;
  }

  [id*="paypal-container"] iframe {
    width: 100% !important;
    max-width: none !important;
  }
`;

const HelpSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: ${colors.base.themeTertiary};
  border-radius: 12px;
  box-shadow: ${colors.effects.shadow};
  text-align: left;

  h3 {
    color: ${colors.base.themePrimary};
    margin: 0 0 12px 0;
    font-size: 1.05rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  p { color: ${colors.base.themePrimary}; font-size: 0.85rem; margin: 0 0 10px 0; }
`;

/* Bridge line between the free-request and contribution sections */
const ConvenienceNote = styled.p`
  color: ${colors.text.light};
  font-size: 0.85rem;
  text-align: center;
  margin: 16px 0 0 0;
  opacity: 0.85;
`;

/* Tile grid — same look as the About modal contribution tiles */
const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin: 12px 0 16px 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.div`
  border: 1px solid rgba(30, 27, 61, 0.15);
  border-radius: 12px;
  padding: 14px 10px;
  text-align: center;
  background: ${colors.base.themeTertiary};
`;

const TileIcon = styled.div`
  width: 28px;
  height: 28px;
  margin: 0 auto 8px;
  color: ${colors.base.themePrimary};

  svg { width: 100%; height: 100%; }
`;

const TileTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${colors.base.themePrimary};
  margin-bottom: 5px;
`;

const TileBody = styled.div`
  font-size: 0.7rem;
  line-height: 1.4;
  color: ${colors.text.dark};
`;

/* Contact button — same accent style as the About modal */
const ContactButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;
  background: ${colors.base.themePrimary};
  color: ${colors.accent.primary};
  border: 1px solid ${colors.accent.secondary};
  border-radius: 24px;
  padding: 10px 22px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 0 14px rgba(255, 255, 255, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  }
`;


const PaymentTitle = styled.h2`
  font-size: 1.6rem;
  color: ${colors.accent.primary};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-weight: 700;
  text-shadow: 0 2px 10px rgba(255, 181, 71, 0.3);

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const PaymentText = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const PremiumFeaturesSection = styled.div`
  margin-top: 25px;
  margin-bottom: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(90deg);
  }
`;

const PremiumButton = ({ 
  user, 
  isAuthenticated, 
  translations,
  onFilterFavorites,
  onFilterProgress,
  onPrintFavorites,
  onPrintProgress,
  isFilteringFavorites,
  isFilteringProgress,
  inline
}) => {
  const { refreshUser } = useAuth();
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [premiumPos, setPremiumPos] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const [isContactOpen, setIsContactOpen] = useState(false);

  const isPremium = user?.isPremium || false;

  // Load PayPal SDK when payment modal opens; load SDK once globally
  useEffect(() => {
    // The SDK load and .render() below are async. Without this guard, an effect
    // re-run or an unmount mid-flight blanks the container while zoid is still
    // rendering into it, and PayPal throws "Detected container element removed
    // from DOM" as an unhandled exception — which CRA shows as a full-screen overlay.
    let cancelled = false;
    let buttons = null;

    const cleanup = () => {
      cancelled = true;
      if (buttons && typeof buttons.close === 'function') {
        try {
          const closing = buttons.close();
          if (closing && typeof closing.catch === 'function') closing.catch(() => {});
        } catch (e) { /* already torn down */ }
      }
      buttons = null;
      const container = document.getElementById('paypal-premium-container');
      if (container) container.innerHTML = '';
    };

    if (!showPaymentModal || paymentSuccess) {
      cleanup();
      return cleanup;
    }

    console.log('Loading PayPal SDK for Premium Button');

    (async () => {
      const ensureSdk = async () => {
        if (window.paypal && window.paypal.Buttons) return;
        if (window.__paypalSdkPromise) { await window.__paypalSdkPromise; return; }
        window.__paypalSdkPromise = (async () => {
          let sdkUrl = 'https://www.paypal.com/sdk/js?client-id=test&components=buttons&currency=CAD';
          try {
            const res = await fetch('/api/config/paypal-url');
            const data = await res.json();
            if (data && data.sdkUrl) sdkUrl = data.sdkUrl;
            console.log('PayPal SDK URL resolved to:', sdkUrl);
          } catch (e) {
            console.warn('Falling back to PayPal demo client-id=test due to config fetch error:', e);
          }
          await new Promise((resolve, reject) => {
            let script = document.getElementById('paypal-sdk');
            if (!script) {
              script = document.createElement('script');
              script.id = 'paypal-sdk';
              script.src = sdkUrl;
              script.async = true;
              script.onload = resolve;
              script.onerror = reject;
              document.body.appendChild(script);
            } else {
              if (window.paypal && window.paypal.Buttons) resolve();
              else script.addEventListener('load', resolve, { once: true });
            }
          });
        })();
        await window.__paypalSdkPromise;
      };

      await ensureSdk();
      if (cancelled) return;

      console.log('PayPal SDK loaded successfully');
      if (window.paypal && window.paypal.Buttons) {
          console.log('Rendering PayPal button for Premium');
          const container = document.getElementById('paypal-premium-container');
          if (!container) return;
          container.innerHTML = '';
          buttons = window.paypal.Buttons({
            fundingSource: window.paypal.FUNDING.PAYPAL,
            createOrder: function(data, actions) {
              const customData = isAuthenticated && user?.userUuid ? user.userUuid : null;
              let amountValue = '1.00';
              const amountInput = document.getElementById('premium-payment-amount');
              if (amountInput && amountInput.value) {
                const parsed = parseFloat(amountInput.value);
                if (!isNaN(parsed) && parsed >= 1) amountValue = parsed.toFixed(2);
              }
              return actions.order.create({
                intent: 'CAPTURE',
                purchase_units: [{ amount: { value: amountValue, currency_code: 'CAD' }, custom_id: customData }]
              });
            },
            onApprove: function(data, actions) {
              return actions.order.capture().then(async function(details) {
                console.log('Payment completed:', details);

                // Extract payer info and send to backend
                const payerEmail = details.payer?.email_address;
                const payerName = details.payer?.name?.given_name
                  ? `${details.payer.name.given_name} ${details.payer.name.surname || ''}`.trim()
                  : null;
                const transactionId = details.purchase_units?.[0]?.payments?.captures?.[0]?.id || details.id;

                console.log('Payer info:', { payerEmail, payerName, transactionId });

                // Update payment with payer info
                if (transactionId && (payerEmail || payerName)) {
                  try {
                    await fetch('/api/payments/update-payer', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ transactionId, payerEmail, payerName })
                    });
                  } catch (err) {
                    console.warn('Could not update payer info:', err);
                  }
                }

                setShowPaymentModal(false);
                setPaymentSuccess(true);
                setShowThankYou(true);
              });
            },
            onError: (err) => {
              console.error('PayPal Buttons error:', err);
              alert('PayPal failed to initialize. Please refresh and try again.');
            }
          });
          buttons.render(container).catch((err) => {
            if (!cancelled) console.error('PayPal render failed:', err);
          });
      } else {
        console.error('PayPal Buttons not available');
      }
    })().catch((err) => {
      if (!cancelled) console.error('PayPal setup failed:', err);
    });

    return cleanup;
  }, [showPaymentModal, paymentSuccess, isAuthenticated, user?.googleId, user?.userUuid]);

  // Calculate dropdown position when menu opens
  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const updateDropdownPosition = () => {
        const rect = buttonRef.current.getBoundingClientRect();
        const isMobile = Math.min(window.innerWidth, window.innerHeight) < 769;
        const dropdownWidth = isMobile ? Math.min(window.innerWidth * 0.85, 240) : 280;
        const margin = 10;
        
        if (isMobile) {
          // Center on mobile
          setDropdownPos({
            top: rect.bottom + 3,
            left: window.innerWidth / 2,
            transform: 'translateX(-50%)'
          });
        } else {
          // Desktop: try to align to right edge of button first
          let left = rect.right - dropdownWidth;
          
          // If it goes off the left side, align to left edge of button
          if (left < margin) {
            left = rect.left;
          }
          
          // If it still goes off the right side, push it back
          if (left + dropdownWidth > window.innerWidth - margin) {
            left = window.innerWidth - dropdownWidth - margin;
          }
          
          // Final safety check for left side
          left = Math.max(margin, left);
          
          setDropdownPos({
            top: rect.bottom + 3,
            left: left
          });
        }
      };
      
      updateDropdownPosition();
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      
      return () => {
        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('scroll', updateDropdownPosition);
      };
    }
  }, [showMenu]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Close premium dropdown or payment modal with Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showPaymentModal) setShowPaymentModal(false);
        if (showMenu) setShowMenu(false);
      }
    };
    if (showMenu || showPaymentModal) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showMenu, showPaymentModal]);

  // Position the premium button to the left of the user profile avatar on desktop
  useEffect(() => {
    const updatePosition = () => {
      // Detect mobile and landscape
      const minDimension = Math.min(window.innerWidth, window.innerHeight);
      const isMobile = minDimension < 769;
      const isLandscape = window.innerHeight < window.innerWidth;
      const isMobileLandscape = isMobile && isLandscape;
      
      // Use dynamic positioning on desktop OR mobile landscape (when buttons are fixed)
      // Keep default fixed position only on mobile portrait (when buttons are in header)
      if (isMobile && !isMobileLandscape) {
        setPremiumPos(null);
        return;
      }
      const profile = document.getElementById('user-profile');
      if (!profile) return;
      const rect = profile.getBoundingClientRect();
      const buttonSize = 36; // CloseButton size and good clickable target
      const spacing = 10;    // Gap between avatar and moon button
      const top = Math.round(rect.top + window.scrollY + (rect.height - buttonSize) / 2) - 6;
      const left = Math.round(rect.left + window.scrollX - spacing - buttonSize);
      setPremiumPos({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true });
    const interval = setInterval(updatePosition, 500); // handle late layout shifts
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  const handlePillowClick = () => {
    // Always refresh user when opening the premium menu to sync latest status
    refreshUser().catch(() => {});
    if (!isAuthenticated) {
      setShowPaymentModal(true);
      return;
    }
    if (isPremium) {
      setShowMenu(!showMenu);
    } else {
      setShowPaymentModal(true);
    }
  };

  const handleMenuItemClick = (action) => {
    action();
    setShowMenu(false);
  };


  // Render premium description with line breaks after each sentence and highlighted phrases
  const renderPremiumDescription = () => {
    const desc = t('premiumDescription') || translations?.premiumDescription || '';
    if (!desc) return null;

    // Phrases to highlight (longest first to avoid partial matches)
    const highlightPhrases = [
      'your input has a real value - it helps make this resource stronger for everyone.',
      'ton retour a une vraie valeur : il nous aide à améliorer cette ressource pour tout le monde.'
    ];

    const highlightStyle = { color: `${colors.accent.primary}`, fontWeight: 600 };
    
    // Split by sentences (period followed by space)
    const sentences = desc.split(/(?<=\.)\s+/);

    const processSentence = (sentence, sIdx) => {
      let parts = [{ text: sentence, highlight: false, index: 0 }];
      
      // Find all highlight phrases in this sentence
      highlightPhrases.forEach(phrase => {
        // Escape special regex characters more thoroughly
        const escapedPhrase = phrase.replace(/[.*+?^${}()|[\]\\&]/g, (match) => {
          if (match === '\\') return '\\\\';
          if (match === '$') return '\\$';
          if (match === '&') return '\\&';
          return '\\' + match;
        });
        const regex = new RegExp(`(${escapedPhrase})`, 'gi');
        const newParts = [];
        
        parts.forEach(part => {
          if (part.highlight) {
            newParts.push(part);
          } else {
            const matches = [...part.text.matchAll(regex)];
            if (matches.length === 0) {
              newParts.push(part);
            } else {
              let lastIndex = 0;
              matches.forEach(match => {
                if (match.index > lastIndex) {
                  newParts.push({
                    text: part.text.substring(lastIndex, match.index),
                    highlight: false,
                    index: part.index + lastIndex
                  });
                }
                newParts.push({
                  text: match[0],
                  highlight: true,
                  index: part.index + match.index
                });
                lastIndex = match.index + match[0].length;
              });
              if (lastIndex < part.text.length) {
                newParts.push({
                  text: part.text.substring(lastIndex),
                  highlight: false,
                  index: part.index + lastIndex
                });
              }
            }
          }
        });
        
        parts = newParts;
      });

      return (
        <div key={sIdx} style={{ marginBottom: '8px' }}>
          {parts.map((part, pIdx) => (
            <span key={`${sIdx}-${pIdx}`} style={part.highlight ? highlightStyle : undefined}>
              {part.text}
            </span>
          ))}
        </div>
      );
    };

    return (
      <PaymentText>
        {sentences.map((sentence, sIdx) => processSentence(sentence, sIdx))}
      </PaymentText>
    );
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <PremiumContainer
        $isAuthenticated={isAuthenticated}
        style={premiumPos ? { top: premiumPos.top, left: premiumPos.left, right: 'auto' } : undefined}
      >
        <PillowButton
          ref={buttonRef}
          $isPremium={isPremium}
          onClick={handlePillowClick}
          title={
            !isAuthenticated
              ? (t('auth.loginWithGoogle') || translations?.loginWithGoogle || 'Login to access Premium')
              : (isPremium
                ? (t('premiumMenu') || translations?.premiumMenu || 'Premium Menu')
                : (t('becomePremium') || translations?.becomePremium || 'Become Premium')
              )
          }
        >
          <IconMoon size={24} />
        </PillowButton>
        <PremiumLabel>Premium{!isPremium && <><br/>(free)</>}</PremiumLabel>
      </PremiumContainer>

      {createPortal(
        <AnimatePresence>
          {showMenu && isPremium && (
            <DropdownMenu
              ref={menuRef}
              style={dropdownPos}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <MenuItem
                $active={isFilteringFavorites}
                onClick={() => handleMenuItemClick(onFilterFavorites)}
              >
                <span><IconStar size={15} /></span>
                {isFilteringFavorites
                  ? (translations?.showAll || "Show all nodes")
                  : (translations?.showFavoritesOnly || "Show favorites only")
                }
              </MenuItem>

              <MenuItem onClick={() => handleMenuItemClick(onPrintFavorites)}>
                <span><IconPrinter size={15} /></span>
                {translations?.printFavorites || "Print favorites"}
              </MenuItem>

              <MenuDivider />

              <MenuItem
                $active={isFilteringProgress}
                onClick={() => handleMenuItemClick(onFilterProgress)}
              >
                <span><IconCheck size={15} /></span>
                {isFilteringProgress
                  ? (translations?.showAll || "Show all nodes")
                  : (translations?.showCompletedOnly || "Show completed only")
                }
              </MenuItem>

              <MenuItem onClick={() => handleMenuItemClick(onPrintProgress)}>
                <span><IconPrinter size={15} /></span>
                {translations?.printCompleted || "Print completed"}
              </MenuItem>
            </DropdownMenu>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        (
          <AnimatePresence>
            {showPaymentModal && !isPremium && (
              <ModalOverlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowPaymentModal(false)}
              >
                <ModalContent
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CloseButton onClick={() => setShowPaymentModal(false)}>×</CloseButton>

                  {!isAuthenticated ? (
                    <>
                      <PaymentTitle><IconMoon size={22} /> Premium</PaymentTitle>
                      <PaymentText>
                        {t('auth.loginWithGoogle') || 'Please log in to access Premium features.'}
                      </PaymentText>
                    </>
                  ) : paymentSuccess ? (
                    <>
                      <PaymentTitle>
                        <span>🎉</span>
                        {translations?.thankYouPayment || 'Thank you so much for your payment!'}
                        <span>🎉</span>
                      </PaymentTitle>
                      <PaymentText>
                        {translations?.paymentNextSteps || 'To activate premium, please log out and log back in. Your premium status will be applied on next login.'}
                      </PaymentText>
                      <div style={{ marginTop: 20, textAlign: 'center' }}>
                        <button
                          onClick={() => setShowPaymentModal(false)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 8,
                            padding: '10px 16px',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          {translations?.close || 'Close'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <PaymentTitle>
                        {t('becomePremiumMember') || translations?.becomePremiumMember || 'Become Premium Member'}
                      </PaymentTitle>

                      <PremiumFeaturesSection>
                        <TileGrid>
                          <Tile>
                            <TileIcon><IconStar /></TileIcon>
                            <TileTitle>{stripEmoji(t('premiumFeatureFavoritesTitle'))}</TileTitle>
                            <TileBody>{stripEmoji(t('premiumFeatureFavoritesFilter'))}</TileBody>
                            <TileBody>{stripEmoji(t('premiumFeatureFavoritesPrint'))}</TileBody>
                          </Tile>
                          <Tile>
                            <TileIcon><IconCheck /></TileIcon>
                            <TileTitle>{stripEmoji(t('premiumFeatureCompletedTitle'))}</TileTitle>
                            <TileBody>{stripEmoji(t('premiumFeatureCompletedFilter'))}</TileBody>
                            <TileBody>{stripEmoji(t('premiumFeatureCompletedPrint'))}</TileBody>
                          </Tile>
                        </TileGrid>
                      </PremiumFeaturesSection>

                      {renderPremiumDescription()}

                      <HelpSection>
                        <h3><IconHeart size={18} /> {t('premiumManualTitle')}</h3>
                        <p>{t('premiumManualIntro')}</p>
                        <TileGrid>
                          <Tile>
                            <TileIcon><IconMoon /></TileIcon>
                            <TileBody>{t('premiumManualItem1')}</TileBody>
                          </Tile>
                          <Tile>
                            <TileIcon><IconHeart /></TileIcon>
                            <TileBody>{t('premiumManualItem2')}</TileBody>
                          </Tile>
                          <Tile>
                            <TileIcon><IconBulb /></TileIcon>
                            <TileBody>{t('premiumManualItem3')}</TileBody>
                          </Tile>
                          <Tile>
                            <TileIcon><IconMail /></TileIcon>
                            <TileBody>{t('premiumManualItem4')}</TileBody>
                          </Tile>
                        </TileGrid>
                        <ContactButton onClick={() => { setShowPaymentModal(false); setTimeout(() => setIsContactOpen(true), 250); }}>
                          <IconMail size={15} /> {t('common.contactMe')}
                        </ContactButton>
                      </HelpSection>

                      <ConvenienceNote>{t('premiumConvenienceNote')}</ConvenienceNote>

                      <DonateSection>
                        <h3><IconStar size={18} /> {t('premiumDonateTitle') || 'Unlock Premium with a voluntary contribution'}</h3>
                        <div style={{ marginBottom: '10px', textAlign: 'left' }}>
                          <input
                            id="premium-payment-amount"
                            type="number"
                            min="1"
                            step="1"
                            defaultValue="1"
                            inputMode="decimal"
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid rgba(30, 27, 61, 0.15)',
                              background: 'white',
                              color: colors.base.themePrimary,
                              fontSize: '0.9rem'
                            }}
                          />
                          <div style={{ color: colors.base.themePrimary, opacity: 0.7, fontSize: '0.7rem', marginTop: '6px', textAlign: 'left' }}>
                            {t('common.minimumAmount')}
                          </div>
                        </div>

                        <div style={{ width: '100%' }}>
                          <div 
                            id="paypal-premium-container" 
                            className="paypal-container"
                            style={{ width: '100%', minHeight: '50px' }}
                          ></div>
                        </div>
                      </DonateSection>
                    </>
                  )}
                </ModalContent>
              </ModalOverlay>
            )}
          </AnimatePresence>
        ),
        document.body
      )}

      <ContactFormModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />

      <PaymentSuccessModal
        isOpen={showThankYou}
        onClose={() => setShowThankYou(false)}
      />
    </>
  );
};

export default PremiumButton;

